import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  User,
  Mail,
  Phone,
  Calendar,
  Users,
  GraduationCap,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { AuthContext } from "../../contexts/AuthContext";
import { getMyStudentProfile } from "../../services/students";
import ServiceWorkerSettings from "../../components/settings/ServiceWorkerSettings";

const StudentProfile: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;

  const {
    data: studentData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["student-profile"],
    queryFn: () => getMyStudentProfile(),
    enabled: !!user?.id,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl border border-red-200/60 rounded-2xl p-6 shadow-lg">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center flex-shrink-0">
              <User className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-red-900 font-semibold text-lg">
                Error Loading Profile
              </h3>
              <p className="text-red-700 mt-2">
                Unable to load your profile information. Please try refreshing
                the page.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const student = studentData?.data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-3 sm:p-4 lg:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            My Profile
          </h1>
          <p className="text-slate-600 mt-2 text-sm sm:text-base">
            View your personal information
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-4 lg:p-6 hover:shadow-xl transition-all duration-300">
              {/* Profile Picture */}
              <div className="text-center mb-6">
                <div className="w-24 h-24 sm:w-28 sm:h-28 mx-auto mb-4 relative">
                  {student?.photoUrl ||
                  student?.userInfo?.profileDetails?.profilePicture ? (
                    <img
                      src={
                        student.photoUrl ||
                        student.userInfo.profileDetails.profilePicture
                      }
                      alt={`${student?.userInfo?.firstName} ${student?.userInfo?.lastName}`}
                      className="w-full h-full rounded-full object-cover ring-4 ring-blue-100 shadow-lg"
                      onError={(e) => {
                        // Fallback to avatar if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = `
                          <div class="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center shadow-lg">
                            <svg class="h-12 w-12 sm:h-14 sm:w-14 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                        `;
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center shadow-lg">
                      <User className="h-12 w-12 sm:h-14 sm:w-14 text-white" />
                    </div>
                  )}
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                  {student?.userInfo?.firstName} {student?.userInfo?.lastName}
                </h2>
                <p className="text-slate-600 font-medium mt-1">Student</p>
                <div className="mt-2 inline-flex items-center px-4 py-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs sm:text-sm font-semibold rounded-lg shadow-md">
                  ID: {student?.studentId || "N/A"}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-slate-200 hover:shadow-md transition-all duration-300">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-300 to-purple-400 flex items-center justify-center shadow-md">
                      <GraduationCap className="h-4 w-4 text-white flex-shrink-0" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">
                      Class
                    </span>
                  </div>
                  <span className="text-sm font-bold text-slate-900 text-right">
                    {(student as any)?.classInfo?.name ||
                      (student as any)?.currentAcademicRecord?.classId?.name ||
                      (student as any)?.classId?.name ||
                      "Not assigned"}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-slate-200 hover:shadow-md transition-all duration-300">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-300 to-blue-400 flex items-center justify-center shadow-md">
                      <Calendar className="h-4 w-4 text-white flex-shrink-0" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">
                      Enrollment
                    </span>
                  </div>
                  <span className="text-sm font-bold text-slate-900 text-right">
                    {student?.enrollmentDate
                      ? new Date(student.enrollmentDate).toLocaleDateString()
                      : "N/A"}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-slate-200 hover:shadow-md transition-all duration-300">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center shadow-md">
                      <Users className="h-4 w-4 text-white flex-shrink-0" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">
                      Status
                    </span>
                  </div>
                  <span
                    className={`text-sm font-semibold px-3 py-1 rounded-lg shadow-md ${
                      (student as any)?.academicStatus === "active" ||
                      student?.isActive
                        ? "bg-gradient-to-r from-emerald-400 to-emerald-500 text-white"
                        : "bg-gradient-to-r from-red-400 to-red-500 text-white"
                    }`}
                  >
                    {(student as any)?.academicStatus
                      ? (student as any).academicStatus
                          .charAt(0)
                          .toUpperCase() +
                        (student as any).academicStatus.slice(1)
                      : student?.isActive
                      ? "Active"
                      : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-2 space-y-4 lg:space-y-6">
            {/* Personal Information */}
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-4 lg:p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-900 flex items-center">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center mr-3 shadow-md">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  Personal Information
                </h3>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:gap-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      First Name
                    </label>
                    <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50 shadow-sm hover:shadow-md transition-all duration-300">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center shadow-md flex-shrink-0">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-slate-900 font-medium truncate">
                        {student?.userInfo?.firstName || "N/A"}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Last Name
                    </label>
                    <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50 shadow-sm hover:shadow-md transition-all duration-300">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center shadow-md flex-shrink-0">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-slate-900 font-medium truncate">
                        {student?.userInfo?.lastName || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Email
                  </label>
                  <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center shadow-md flex-shrink-0">
                      <Mail className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-slate-900 font-medium">
                      {student?.userInfo?.email || "N/A"}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Admission Number
                  </label>
                  <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center shadow-md flex-shrink-0">
                      <GraduationCap className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-slate-900 font-medium">
                      {student?.admissionNumber || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Emergency Contact Information */}
            {student?.parentGuardianInfo?.emergencyContact && (
              <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-xl transition-all duration-300">
                <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-300 to-red-400 flex items-center justify-center mr-3 shadow-md">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  Emergency Contact Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Name
                    </label>
                    <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-xl bg-gradient-to-br from-slate-50 to-red-50 shadow-sm hover:shadow-md transition-all duration-300">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-300 to-red-400 flex items-center justify-center shadow-md flex-shrink-0">
                        <Users className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-slate-900 font-medium">
                        {student.parentGuardianInfo.emergencyContact.name ||
                          "N/A"}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Relationship
                    </label>
                    <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-xl bg-gradient-to-br from-slate-50 to-red-50 shadow-sm hover:shadow-md transition-all duration-300">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-300 to-red-400 flex items-center justify-center shadow-md flex-shrink-0">
                        <Users className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-slate-900 font-medium">
                        {student.parentGuardianInfo.emergencyContact
                          .relationship || "N/A"}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Primary Phone
                    </label>
                    <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-xl bg-gradient-to-br from-slate-50 to-red-50 shadow-sm hover:shadow-md transition-all duration-300">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-300 to-red-400 flex items-center justify-center shadow-md flex-shrink-0">
                        <Phone className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-slate-900 font-medium">
                        {student.parentGuardianInfo.emergencyContact.phone ||
                          "N/A"}
                      </span>
                    </div>
                  </div>

                  {student.parentGuardianInfo.emergencyContact
                    .alternatePhone && (
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Alternate Phone
                      </label>
                      <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-xl bg-gradient-to-br from-slate-50 to-red-50 shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-300 to-red-400 flex items-center justify-center shadow-md flex-shrink-0">
                          <Phone className="h-4 w-4 text-white" />
                        </div>
                        <span className="text-slate-900 font-medium">
                          {
                            student.parentGuardianInfo.emergencyContact
                              .alternatePhone
                          }
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Academic Records */}
            {student?.academicRecords && student.academicRecords.length > 0 && (
              <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-xl transition-all duration-300">
                <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center mr-3 shadow-md">
                    <GraduationCap className="h-5 w-5 text-white" />
                  </div>
                  Academic Records
                </h3>

                <div className="space-y-4">
                  {student.academicRecords.map((record: any, index: number) => (
                    <div
                      key={index}
                      className="p-5 border border-slate-200 rounded-xl bg-gradient-to-br from-slate-50 to-indigo-50 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-slate-900 text-lg flex items-center">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center mr-2 shadow-md">
                            <span className="text-white text-xs font-bold">
                              {index + 1}
                            </span>
                          </div>
                          {record.term || `Term ${index + 1}`}
                        </h4>
                        <span className="text-sm font-semibold bg-gradient-to-r from-blue-300 to-blue-400 text-white px-3 py-1.5 rounded-lg shadow-md">
                          Class: {record.classId || "N/A"}
                        </span>
                      </div>
                      {record.grades &&
                      Object.keys(record.grades).length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {Object.entries(record.grades).map(
                            ([subject, grade]) => (
                              <div
                                key={subject}
                                className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-all duration-300"
                              >
                                <span className="text-sm font-medium text-slate-700">
                                  {subject}
                                </span>
                                <span className="text-sm font-bold bg-gradient-to-r from-emerald-400 to-emerald-500 text-white px-2.5 py-1 rounded-lg shadow-md">
                                  {String(grade)}
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-sm text-slate-600 font-medium">
                            No grades recorded
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* App Settings Section */}
        <div className="mt-6">
          <ServiceWorkerSettings />
        </div>
      </div>
    </div>
  );
};

export default StudentProfile;
